// frontend/src/components/common/CommonComponents.js
import React, { useState, useEffect, useRef } from 'react';

// ── Currency formatter ────────────────────────────────────────
export const fmt = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 });

export const fmtK = (n) => {
  n = Number(n || 0);
  if (Math.abs(n) >= 1e9) return "₦" + (n / 1e9).toFixed(1) + "B";
  if (Math.abs(n) >= 1e6) return "₦" + (n / 1e6).toFixed(1) + "M";
  if (Math.abs(n) >= 1e3) return "₦" + (n / 1e3).toFixed(1) + "K";
  return fmt(n);
};

// ── PayBadge ──────────────────────────────────────────────────
export function PayBadge({ status }) {
  if (!status) return <span className="badge badge-active">---</span>;
  const map = {
    Paid: "paid", "Partially Paid": "partial", Unpaid: "unpaid",
    Active: "active", Completed: "completed", Ongoing: "ongoing",
    Cleared: "cleared", Approved: "approved", Rejected: "rejected",
    Pending: "pending", "In Production": "in-production",
    Ready: "ready", Delivered: "delivered",
    "Awaiting Payment": "awaiting-payment", Cancelled: "cancelled",
  };
  return <span className={`badge badge-${map[status] || "active"}`}>{status}</span>;
}

// ── StatCard ──────────────────────────────────────────────────
export function StatCard({ label, value, cls, sub }) {
  return (
    <div className="stat-card">
      <div className="lbl">{label}</div>
      <div className={`val ${cls || ""}`}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Pager ─────────────────────────────────────────────────────
export function Pager({ page, total, per, onChange }) {
  const pages = Math.ceil(total / per);
  if (pages <= 1) return null;
  return (
    <div className="pager">
      <button className="btn-sm btn-secondary" onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1}>‹ Prev</button>
      <span>Page {page} of {pages}</span>
      <button className="btn-sm btn-secondary" onClick={() => onChange(Math.min(pages, page + 1))} disabled={page === pages}>Next ›</button>
    </div>
  );
}

// ── CollapsibleCard ──────────────────────────────────────────
export function CollapsibleCard({ title, defaultOpen = true, children, headerRight, style = {} }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card" style={style}>
      <div className="card-collapsible-header" onClick={() => setOpen(o => !o)}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {headerRight && <div onClick={e => e.stopPropagation()}>{headerRight}</div>}
          <span className="card-collapse-icon">{open ? "▲" : "▼"}</span>
        </div>
      </div>
      {open && <div className="card-body">{children}</div>}
    </div>
  );
}

// ── Date helpers ─────────────────────────────────────────────────
export const today = () => new Date().toISOString().split("T")[0];
export const toDate = (d) => d ? new Date(d).toISOString().split("T")[0] : "";

// ── Print functions ─────────────────────────────────────────────
// All three take a `hasCustomBranding` flag (from user.features on the
// caller's side) as their last argument. Tier 10: custom branding
// (a business's own name/logo appearing on documents it hands to
// customers) is a Growth/Enterprise feature — Trial and Starter
// businesses print with generic "Wavelines ERP" branding instead,
// regardless of what's stored in businessName/businessLogo. This is
// deliberately enforced at print time here, not just at upload time
// (PUT /api/business/logo) — a business that had branding set before an
// upgrade lapsed, or one from before this restriction existed, still
// gets the correct output without needing any data cleanup.
const WAVELINES_BRAND_NAME = "Wavelines ERP";

export const printInvoice = (order, businessName, businessLogo, hasCustomBranding) => {
  const win = window.open("", "_blank", "width=800,height=600");
  const displayName = hasCustomBranding ? businessName : WAVELINES_BRAND_NAME;
  const displayLogo = hasCustomBranding ? businessLogo : "";
  const logo = displayLogo ? `<img src="${displayLogo}" style="height:60px;object-fit:contain;"/>` : "";
  
  win.document.write(`
    <!DOCTYPE html><html><head><title>Invoice --- ${order.orderId}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Segoe UI',sans-serif;color:#1a1a2e;padding:40px;max-width:700px;margin:0 auto}
      .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px;padding-bottom:20px;border-bottom:2px solid #1a1a2e}
      .biz-name{font-size:22px;font-weight:700;color:#1a1a2e}
      .biz-sub{font-size:12px;color:#6b7280;margin-top:4px}
      .invoice-label{font-size:28px;font-weight:700;color:#e94560;text-align:right}
      .invoice-num{font-size:13px;color:#6b7280;text-align:right;margin-top:4px}
      .meta{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:28px}
      .meta-block label{font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:#9ca3af;display:block;margin-bottom:4px}
      .meta-block p{font-size:14px;font-weight:500}
      table{width:100%;border-collapse:collapse;margin-bottom:24px}
      th{background:#1a1a2e;color:#fff;padding:10px 12px;text-align:left;font-size:12px}
      td{padding:10px 12px;border-bottom:1px solid #f3f4f6;font-size:13px}
      .totals{border-top:2px solid #1a1a2e;padding-top:16px}
      .total-row{display:flex;justify-content:space-between;padding:4px 0;font-size:13px}
      .total-row.grand{font-size:16px;font-weight:700;color:#1a1a2e;margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb}
      .paid-row{color:#10b981;font-weight:600}
      .balance-row{color:#e94560;font-weight:700}
      .badge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700}
      .badge-paid{background:#d1fae5;color:#065f46}
      .badge-partial{background:#fef3c7;color:#92400e}
      .badge-unpaid{background:#fee2e2;color:#991b1b}
      .footer{margin-top:40px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;text-align:center}
      @media print{body{padding:20px}.no-print{display:none}}
    </style>
  `);
  
  win.document.write(`
    <div class="no-print" style="text-align:right;margin-bottom:16px">
      <button onclick="window.print()" style="padding:8px 20px;background:#1a1a2e;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px">🖨 Print Invoice</button>
      <button onclick="window.close()" style="padding:8px 20px;background:#f3f4f6;border:none;border-radius:6px;cursor:pointer;font-size:13px;margin-left:8px">✕ Close</button>
    </div>
    <div class="header">
      <div>${logo}<div class="biz-name">${displayName}</div><div class="biz-sub">Business Management System</div></div>
      <div><div class="invoice-label">INVOICE</div><div class="invoice-num">${order.orderId}</div><div class="invoice-num">Date: ${new Date(order.createdAt || Date.now()).toLocaleDateString("en-NG", {day:"numeric",month:"long",year:"numeric"})}</div></div>
    </div>
    <div class="meta">
      <div class="meta-block"><label>Bill To</label><p>${order.client}</p></div>
      <div class="meta-block"><label>Production Status</label><p>${order.productionStatus || "Pending"}</p></div>
      <div class="meta-block"><label>Payment Status</label><p><span class="badge badge-${order.paymentStatus==="Paid"?"paid":order.paymentStatus==="Partially Paid"?"partial":"unpaid"}">${order.paymentStatus}</span></p></div>
      <div class="meta-block"><label>Due Date</label><p>Upon delivery</p></div>
    </div>
    <table>
      <thead><tr><th>Description</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit Price</th><th style="text-align:right">Amount</th></tr></thead>
      <tbody>
        <tr>
          <td><strong>${order.item}</strong>${order.description?`<br/><span style="font-size:11px;color:#6b7280">${order.description}</span>`:""}</td>
          <td style="text-align:right">${order.quantity}</td>
          <td style="text-align:right">₦${Number(order.sellingPrice || 0).toLocaleString("en-NG", {minimumFractionDigits:2})}</td>
          <td style="text-align:right">₦${Number(order.totalSales || 0).toLocaleString("en-NG", {minimumFractionDigits:2})}</td>
        </tr>
      </tbody>
    </table>
    <div class="totals">
      <div class="total-row grand"><span>Invoice Total</span><span>₦${Number(order.totalSales || 0).toLocaleString("en-NG", {minimumFractionDigits:2})}</span></div>
      <div class="total-row paid-row"><span>Amount Paid</span><span>₦${Number(order.totalPaid || 0).toLocaleString("en-NG", {minimumFractionDigits:2})}</span></div>
      ${Number(order.balance || 0) > 0 ? `<div class="total-row balance-row"><span>Balance Due</span><span>₦${Number(order.balance || 0).toLocaleString("en-NG", {minimumFractionDigits:2})}</span></div>` : ""}
    </div>
    <div class="footer">Thank you for your business · ${displayName} · Powered by Wavelines ERP</div>
  `);
  
  win.document.close();
};

// Creates a compact, portable document image for a customer's WhatsApp or
// other share target. It intentionally uses Canvas rather than a screenshot
// dependency, so it works in the existing web app and can use the device's
// native share sheet on mobile.
export const shareOrderDocumentImage = async (order, documentType, businessName, hasCustomBranding) => {
  const isReceipt = documentType === "receipt";
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1280;
  const ctx = canvas.getContext("2d");
  const brand = hasCustomBranding ? (businessName || WAVELINES_BRAND_NAME) : WAVELINES_BRAND_NAME;
  const amount = isReceipt ? Number(order.totalPaid || 0) : Number(order.totalSales || 0);
  const title = isReceipt ? "PAYMENT RECEIPT" : "INVOICE";
  const naira = value => `₦${Number(value || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
  const line = (label, value, y, emphasize = false) => {
    ctx.fillStyle = "#6b7280";
    ctx.font = "28px Arial";
    ctx.fillText(label, 80, y);
    ctx.fillStyle = "#111827";
    ctx.font = emphasize ? "bold 34px Arial" : "30px Arial";
    ctx.textAlign = "right";
    ctx.fillText(value, 1000, y);
    ctx.textAlign = "left";
    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(80, y + 22); ctx.lineTo(1000, y + 22); ctx.stroke();
  };

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#111827";
  ctx.font = "bold 46px Arial";
  ctx.fillText(brand, 80, 100);
  ctx.fillStyle = "#e94560";
  ctx.font = "bold 48px Arial";
  ctx.textAlign = "right";
  ctx.fillText(title, 1000, 100);
  ctx.textAlign = "left";
  ctx.strokeStyle = "#111827";
  ctx.lineWidth = 4;
  ctx.beginPath(); ctx.moveTo(80, 135); ctx.lineTo(1000, 135); ctx.stroke();

  ctx.fillStyle = "#374151";
  ctx.font = "30px Arial";
  ctx.fillText(`${isReceipt ? "Receipt" : "Invoice"} number: ${order.orderId}`, 80, 195);
  ctx.fillText(`Date: ${new Date(order.createdAt || Date.now()).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" })}`, 80, 240);
  line("Customer", order.client || "—", 330);
  line("Item", `${order.item || "—"} × ${Number(order.quantity || 0)}`, 410);
  line(isReceipt ? "Total received" : "Invoice total", naira(amount), 510, true);
  if (!isReceipt) {
    line("Amount paid", naira(order.totalPaid), 590);
    line("Balance", naira(Math.max(Number(order.balance || 0), 0)), 670, true);
  } else {
    line("Order status", order.productionStatus === "Delivered" ? "Delivered and closed" : (order.paymentStatus || "Recorded"), 590);
  }
  ctx.fillStyle = "#6b7280";
  ctx.font = "26px Arial";
  ctx.textAlign = "center";
  ctx.fillText(`Generated by ${brand}`, 540, 1180);
  ctx.textAlign = "left";

  const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
  if (!blob) throw new Error("Could not create the document image.");
  const file = new File([blob], `${isReceipt ? "receipt" : "invoice"}-${order.orderId}.png`, { type: "image/png" });
  if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
    await navigator.share({ title: `${title} ${order.orderId}`, files: [file] });
    return;
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  link.click();
  URL.revokeObjectURL(url);
};

export const printReceipt = (payment, order, businessName, businessLogo, hasCustomBranding) => {
  const win = window.open("", "_blank", "width=500,height=500");
  const displayName = hasCustomBranding ? businessName : WAVELINES_BRAND_NAME;
  const displayLogo = hasCustomBranding ? businessLogo : "";
  const logo = displayLogo ? `<img src="${displayLogo}" style="height:50px;object-fit:contain;"/>` : "";
  
  const date = new Date(payment.date || Date.now()).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" });
  
  const orderStatusHtml = order && Number(order.balance || 0) > 0
    ? `<div class="row" style="color:#e94560"><span>Remaining Balance</span><span>₦${Number(order.balance || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</span></div>`
    : `<div class="row" style="color:#10b981;font-weight:600"><span>Order Status</span><span>✓ Fully Paid</span></div>`;
  
  win.document.write(`
    <!DOCTYPE html><html><head><title>Receipt --- ${payment.paymentId}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Segoe UI',sans-serif;color:#1a1a2e;padding:30px;max-width:420px;margin:0 auto}
      .center{text-align:center}
      .biz-name{font-size:18px;font-weight:700;margin:8px 0 2px}
      .title{font-size:13px;color:#6b7280;margin-bottom:20px}
      .receipt-id{background:#f9fafb;border-radius:8px;padding:10px;text-align:center;margin-bottom:16px;font-family:monospace;font-size:14px;font-weight:600;letter-spacing:1px}
      .row{display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px solid #f3f4f6;font-size:13px}
      .row.total{font-size:16px;font-weight:700;border-top:2px solid #1a1a2e;border-bottom:none;margin-top:8px;padding-top:12px}
      .green{color:#10b981}
      .footer{margin-top:24px;font-size:11px;color:#9ca3af;text-align:center;line-height:1.6}
      @media print{body{padding:10px}.no-print{display:none}}
    </style>
  `);
  
  win.document.write(`
    <div class="no-print" style="text-align:right;margin-bottom:12px">
      <button onclick="window.print()" style="padding:6px 16px;background:#1a1a2e;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:12px">🖨 Print</button>
      <button onclick="window.close()" style="padding:6px 16px;background:#f3f4f6;border:none;border-radius:6px;cursor:pointer;font-size:12px;margin-left:6px">✕ Close</button>
    </div>
    <div class="center">${logo}<div class="biz-name">${displayName}</div><div class="title">PAYMENT RECEIPT</div></div>
    <div class="receipt-id">${payment.paymentId}</div>
    <div class="row"><span>Order</span><span>${order?.orderId || payment.orderId}</span></div>
    <div class="row"><span>Client</span><span>${order?.client || "---"}</span></div>
    <div class="row"><span>Item</span><span style="max-width:180px;text-align:right">${order?.item || "---"}</span></div>
    <div class="row"><span>Date</span><span>${date}</span></div>
    <div class="row"><span>Method</span><span>${payment.method}</span></div>
    <div class="row total"><span>Amount Paid</span><span class="green">₦${Number(payment.amount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</span></div>
    ${orderStatusHtml}
    <div class="footer">Thank you for your payment<br/>${displayName} · Powered by Wavelines ERP<br/>${new Date().toLocaleString("en-NG")}</div>
  `);
  
  win.document.close();
};

// mode: "receipt" (default — a completed, paid sale) or "invoice" (a
// sale still Awaiting Payment — see routes/sales.js: stock isn't even
// deducted until payment is confirmed, so this document exists purely
// to give the customer something to pay against, not proof of payment
// already received). Same layout either way; only the framing text
// changes, so a business's paper trail looks consistent whichever one
// prints.
export const printSaleReceipt = (sale, businessName, businessLogo, hasCustomBranding, mode = "receipt") => {
  const win = window.open("", "_blank", "width=500,height=600");
  const displayName = hasCustomBranding ? businessName : WAVELINES_BRAND_NAME;
  const displayLogo = hasCustomBranding ? businessLogo : "";
  const logo = displayLogo ? `<img src="${displayLogo}" style="height:50px;object-fit:contain;"/>` : "";
  const isInvoice = mode === "invoice";
  
  const date = new Date(sale.saleDate || Date.now()).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" });
  
  const itemsHtml = (sale.items || []).map(i => 
    `<div class="row"><span>${i.productName} × ${i.quantity}</span><span>₦${Number(i.subtotal || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</span></div>`
  ).join("");
  
  const discountHtml = Number(sale.discountAmount || 0) > 0
    ? `<div class="row discount"><span>Discount${sale.discountReason ? ` (${sale.discountReason})` : ""}</span><span>−₦${Number(sale.discountAmount).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</span></div>`
    : "";
  
  const referenceHtml = sale.paymentReference
    ? `<div class="row"><span>Reference</span><span>${sale.paymentReference}</span></div>`
    : "";
  
  win.document.write(`
    <!DOCTYPE html><html><head><title>${isInvoice ? "Invoice" : "Receipt"} --- ${sale.saleId}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'Segoe UI',sans-serif;color:#1a1a2e;padding:30px;max-width:420px;margin:0 auto}
      .center{text-align:center}
      .biz-name{font-size:18px;font-weight:700;margin:8px 0 2px}
      .title{font-size:13px;color:#6b7280;margin-bottom:20px}
      .receipt-id{background:#f9fafb;border-radius:8px;padding:10px;text-align:center;margin-bottom:16px;font-family:monospace;font-size:14px;font-weight:600;letter-spacing:1px}
      .row{display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px solid #f3f4f6;font-size:13px}
      .row.total{font-size:16px;font-weight:700;border-top:2px solid #1a1a2e;border-bottom:none;margin-top:8px;padding-top:12px}
      .row.discount{color:#e94560}
      .green{color:#10b981}
      .amber{color:#f59e0b}
      .footer{margin-top:24px;font-size:11px;color:#9ca3af;text-align:center;line-height:1.6}
      @media print{body{padding:10px}.no-print{display:none}}
    </style>
  `);
  
  win.document.write(`
    <div class="no-print" style="text-align:right;margin-bottom:12px">
      <button onclick="window.print()" style="padding:6px 16px;background:#1a1a2e;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:12px">🖨 Print</button>
      <button onclick="window.close()" style="padding:6px 16px;background:#f3f4f6;border:none;border-radius:6px;cursor:pointer;font-size:12px;margin-left:6px">✕ Close</button>
    </div>
    <div class="center">${logo}<div class="biz-name">${displayName}</div><div class="title">${isInvoice ? "SALE INVOICE" : "SALE RECEIPT"}</div></div>
    <div class="receipt-id">${sale.saleId}</div>
    <div class="row"><span>Date</span><span>${date}</span></div>
    <div class="row"><span>${isInvoice ? "Payment Method (proposed)" : "Payment Method"}</span><span>${sale.paymentMethod}</span></div>
    ${referenceHtml}
    <div style="height:8px"></div>
    ${itemsHtml}
    ${discountHtml}
    <div class="row total"><span>${isInvoice ? "Amount Due" : "Total Paid"}</span><span class="${isInvoice ? "amber" : "green"}">₦${Number(sale.totalAmount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}</span></div>
    <div class="footer">${isInvoice ? "Awaiting payment confirmation — this is not proof of payment received." : "Thank you for your purchase!"}<br/>${displayName} · Powered by Wavelines ERP<br/>${new Date().toLocaleString("en-NG")}</div>
  `);
  
  win.document.close();
};

// ── SearchableDropdown ──────────────────────────────────────────
export function SearchableDropdown({ label, placeholder, value, onSelect, onClear, items, renderItem, renderLabel, emptyMsg, disabled }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = query
    ? items.filter(item => renderLabel(item).toLowerCase().includes(query.toLowerCase()))
    : items;

  const displayValue = value ? renderLabel(value) : "";

  return (
    <div className="form-group" ref={ref} style={{ position: "relative" }}>
      <label>{label}</label>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          value={value ? displayValue : query}
          onChange={e => { setQuery(e.target.value); if (value) onClear(); setOpen(true); }}
          onFocus={() => { setOpen(true); }}
          placeholder={placeholder}
          disabled={disabled}
          style={{ flex: 1, padding: "10px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 14, outline: "none", background: "#fff" }}
        />
        {value && (
          <button onClick={() => { onClear(); setQuery(""); }} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px" }} title="Clear">✕</button>
        )}
      </div>
      {open && !value && (
        <div style={{
          position: "absolute", left: 0, right: 0, top: "calc(100% + 2px)",
          background: "#fff", border: "1.5px solid #e94560", borderRadius: 8,
          boxShadow: "0 6px 20px rgba(0,0,0,.12)", zIndex: 200,
          maxHeight: 220, overflowY: "auto",
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: "12px 14px", color: "#9ca3af", fontSize: 13, textAlign: "center" }}>
              {emptyMsg || "No results found"}
            </div>
          ) : filtered.map((item, idx) => (
            <button
              key={idx}
              onMouseDown={e => { e.preventDefault(); onSelect(item); setQuery(""); setOpen(false); }}
              style={{
                display: "block", width: "100%", padding: "10px 14px",
                background: "none", border: "none", borderBottom: "1px solid #f3f4f6",
                cursor: "pointer", textAlign: "left", fontSize: 13,
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#fff8f9"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              {renderItem(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
